import { getActionLogRecordedSettings, getLogFileName } from './actionLogPresentation';

// These are presentation contracts for the native forms, not execution rules.
const EXCEL_FIELDS = {
  sourceFilePath: ['source_label', 'file_section_title', 'file'],
  target: ['target_label', 'file_section_title', 'file'],
  sheetName: ['sheet_label', 'file_section_title'],
  searchKeyword: ['search_keyword_label', 'match_title'],
  searchColumn: ['search_column_label', 'match_title'],
  columnName: ['column_name_label', 'match_title'],
  searchRow: ['search_row_label', 'match_title'],
  searchMode: ['search_mode_label', 'match_title', 'searchMode'],
  startColumn: ['start_column_label', 'position_title'],
  insertAtRow: ['insert_at_label', 'position_title'],
  insertAtColumn: ['insert_at_label', 'position_title'],
  valueRow: ['value_row_label', 'position_title'],
  addIfNotFound: ['add_if_not_found_label', 'position_title'],
  addIfNotFoundMode: ['add_new_column_mode_label', 'position_title', 'addMode'],
  rowValues: ['row_values_label', 'row_title'],
  columnValue: ['column_value_label', 'column_title'],
};
const CONDITION_OPERATORS = {
  equals: 'equals', not_equals: 'not_equals', contains: 'contains', not_contains: 'not_contains',
  startswith: 'startswith', endswith: 'endswith', not_empty: 'not_empty', is_empty: 'is_empty',
  more: 'more', less: 'less', less_or_equal: 'less_or_equal',
};

export const getActionLogFormSettings = (log, t, catalogEntry) => {
  const recorded = getActionLogRecordedSettings(log, { limit: 80, includePaths: true });
  const scalar = new Map(recorded.values.filter(entry => entry.path.length === 1).map(entry => [entry.path[0], entry.value]));
  const sections = new Map();
  let partial = recorded.partial;
  let count = 0;
  const text = value => value === null || value === '' ? t('actionsLogs.activity.notSpecified', 'Not specified')
    : typeof value === 'boolean' ? t(`actionsLogs.activity.${value ? 'yes' : 'no'}`, value ? 'Yes' : 'No') : String(value);
  const add = (section, label, value) => {
    if (count >= 16) { partial = true; return; }
    if (!sections.has(section)) sections.set(section, []);
    sections.get(section).push({ label, value: text(value) });
    count += 1;
  };
  const settingsSection = t('actionsLogs.activity.formSettings', 'Action settings');
  const excel = log.action_type === 'addexcelrow' ? 'add_excel_row' : log.action_type === 'addexcelcolumn' ? 'add_excel_column' : null;
  const pluginFields = catalogEntry && catalogEntry.type === log.action_type && Array.isArray(catalogEntry.settings?.fields)
    ? catalogEntry.settings.fields : [];
  const nativeFields = excel ? Object.entries(EXCEL_FIELDS).map(([key, [label, section, format]]) => {
    const labelKey = key === 'addIfNotFound' ? `add_new_${excel === 'add_excel_row' ? 'row' : 'column'}_if_not_found_label`
      : key === 'addIfNotFoundMode' ? `add_new_${excel === 'add_excel_row' ? 'row' : 'column'}_mode_label` : label;
    return { key, label: t(`workflow.${excel}.${labelKey}`, ''), section: t(`workflow.${excel}.${section}`, settingsSection), format };
  }) : [];
  const fields = pluginFields.length ? pluginFields : nativeFields;
  const handled = new Set(['description', 'useConditions', 'conditions', 'conditionLogic']);
  for (const field of fields.slice(0, 100)) {
    if (!field || typeof field !== 'object' || typeof field.key !== 'string') continue;
    handled.add(field.key);
    const fieldType = String(field.type || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
    if (!scalar.has(field.key) || field.sensitive || fieldType === 'password' || typeof field.label !== 'string' || !field.label) continue;
    if (['rowValues', 'columnValue'].includes(field.key) && recorded.values.some(entry => entry.path[0] === (field.key === 'rowValues' ? 'rowValueRows' : 'columnValueRows'))) continue;
    let value = scalar.get(field.key);
    if (field.key === 'addIfNotFoundMode' && scalar.get('addIfNotFound') === false) continue;
    if (field.format === 'file' || ['file', 'filepath', 'folder', 'folderpath', 'path'].includes(fieldType)
      || field.pathField === true || field.isPath === true) value = getLogFileName(value);
    if (field.format === 'searchMode' && ['contains', 'exact', 'starts_with'].includes(value)) {
      value = t(`workflow.${excel}.search_mode_${value}`, value);
    }
    if (field.format === 'addMode' && ['append_end', 'insert_by_order'].includes(value)) {
      value = t(`workflow.${excel}.add_new_${excel === 'add_excel_row' ? 'row' : 'column'}_mode_${value}`, value);
    }
    const option = Array.isArray(field.options) && field.options.find(option => option && typeof option === 'object' && option.value === value);
    if (typeof option?.label === 'string') value = option.label;
    add(typeof field.section === 'string' ? field.section : settingsSection, field.label, value);
  }
  // Preserve the form's cell order and its explicit "keep current value" choice.
  for (const [key, labelKey, sectionKey] of [['rowValueRows', 'row_values_label', 'row_title'], ['columnValueRows', 'column_value_label', 'column_title']]) {
    if (!excel) continue;
    handled.add(key);
    const cells = new Map();
    recorded.values.filter(entry => entry.path[0] === key && [2, 3].includes(entry.path.length)).forEach(entry => {
      const [, index, property] = entry.path;
      if (!cells.has(index)) cells.set(index, {});
      if (entry.path.length === 2) cells.get(index).value = entry.value;
      else if (['value', 'keepExisting'].includes(property)) cells.get(index)[property] = entry.value;
    });
    for (const [index, cell] of cells) add(t(`workflow.${excel}.${sectionKey}`, settingsSection),
      `${t(`workflow.${excel}.${labelKey}`, 'Values')} · ${Number(index) + 1}`,
      cell.keepExisting === true ? t('workflow.add_excel_column.keep_existing_label', 'Keep current value') : cell.value ?? '');
  }
  const conditionsSection = t('dag.flow_assistant.runs_when', 'Runs when');
  const conditions = new Map();
  recorded.values.filter(entry => entry.path[0] === 'conditions' && entry.path.length === 3).forEach(entry => {
    const [, index, property] = entry.path;
    if (!conditions.has(index)) conditions.set(index, {});
    if (['tag', 'condition', 'value'].includes(property)) conditions.get(index)[property] = entry.value;
  });
  if (scalar.get('useConditions') === false) {
    add(conditionsSection, conditionsSection, t('dag.flow_assistant.always_runs', 'No condition — runs when this step is reached.'));
  } else if (conditions.size) {
    const logic = scalar.get('conditionLogic');
    const knownLogic = conditions.size === 1 || ['AND', 'OR'].includes(logic);
    if (!knownLogic) partial = true;
    else {
      const label = logic === 'OR' ? t('dag.flow_assistant.condition_any', 'At least one condition must pass')
        : t('dag.flow_assistant.condition_all', 'Every condition must pass');
      for (const condition of conditions.values()) {
        const operator = Object.prototype.hasOwnProperty.call(CONDITION_OPERATORS, condition.condition) && CONDITION_OPERATORS[condition.condition];
        if (!operator || !condition.tag) { partial = true; continue; }
        if (!['not_empty', 'is_empty'].includes(operator) && !Object.prototype.hasOwnProperty.call(condition, 'value')) { partial = true; continue; }
        const comparison = ['not_empty', 'is_empty'].includes(operator) ? '' : ` ${text(condition.value)}`;
        add(conditionsSection, label, `${condition.tag} ${t(`dag.flow_assistant.condition_operator.${operator}`, operator)}${comparison}`);
      }
    }
  }
  if (recorded.values.some(entry => !handled.has(entry.path[0]))) partial = true;
  return { sections: [...sections].map(([title, fields]) => ({ title, fields })), partial, hasRecordedValues: recorded.values.length > 0 };
};
